import React from "react";
//#region types
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { TimeReportingProjectFormViewProps } from "models/propTypes/timeReportingProjectPropTypes";
import {
  EditableTableMoreActionMenuItemType,
  GenericFormSection,
  TableListFieldProps,
} from "models/propTypes/genericFormProps";
import { FormEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { FormikProps, useFormikContext } from "formik";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { StaticInfoMessage } from "components/common";
import ProjectCategoryStatusTags from "../projectCategoryStatusTags";
import TimeReportingProjectCategoryFormContainer from "../forms/categories/timeReportingProjectCategoryFormContainer";
import { findSelfBillingMembersMissingRateForNewCategories } from "./selfBillingValidation";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";

const SelfBillingMissingRateInfoBanner: React.FC = () => {
  const formik = useFormikContext<TimeReportingProjectFormTypes.Create>();
  const missing = findSelfBillingMembersMissingRateForNewCategories(formik.values);
  if (missing.length === 0) return null;
  return (
    <StaticInfoMessage gutterBottom>
      {LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.TimeReportingProject.SelfBillingMissingHourlyRateForCategory,
        undefined,
        { names: missing.join(", ") }
      )}
    </StaticInfoMessage>
  );
};

function useProjectCategoryFields(
  props: TimeReportingProjectFormViewProps
): GenericFormSection<TimeReportingProjectFormTypes.Create>[] {
  const getCategoriesMoreActions = (
    row: TimeReportingProjectFormTypes.ProjectCategory,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>
  ) => {
    const { values } = formikProps;
    const actions: EditableTableMoreActionMenuItemType[] = [];

    if (!row.isDefault) {
      actions.push({
        description: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReportingProject.SetAsStandardCategory
        ),
        icon: IconDefinitionConstants.WCheck,
        onClickHandler: onSetCategoryAsDefault,
      });
    }

    if (
      row.id === undefined &&
      values.projectCategories?.length &&
      values.projectCategories?.length > 1 &&
      !row.isDefault
    ) {
      actions.push({
        description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Remove),
        icon: IconDefinitionConstants.WTrashAlt,
        onClickHandler: onDeleteCategory,
      });
    }

    const isActive = row.endDate
      ? DateHelper.isBefore(new Date(), new Date(row.endDate)) || DateHelper.isEqual(new Date(), new Date(row.endDate))
      : true;
    const isProjectActive = DateHelper.withinIntervall(
      new Date(),
      values.projectStartDate as Date,
      values.projectEndDate as Date
    );

    if (isProjectActive) {
      if (isActive) {
        actions.push({
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReportingProject.InactivateCategory
          ),
          icon: IconDefinitionConstants.WStop,
          onClickHandler: onDeactivateCategory,
        });
      } else {
        actions.push({
          description: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReportingProject.ActivateCategory
          ),
          icon: IconDefinitionConstants.WCheck,
          onClickHandler: onActivateCategory,
        });
      }
    }

    return actions;
  };

  const onSetCategoryAsDefault = async (
    rowIndex: number,
    row: TimeReportingProjectFormTypes.ProjectCategory,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>
  ) => {
    const { values, setFieldValue, setFieldTouched } = formikProps;

    if (values.projectCategories == null) {
      return;
    }

    const updatedCategories = values.projectCategories.map((pc) => ({ ...pc, isDefault: pc.name === row.name }));

    setFieldValue(FormFieldConstants.TimeReportingProject.Create.ProjectCategories, updatedCategories);
    setFieldTouched(FormFieldConstants.TimeReportingProject.Create.ProjectCategories, true, false);
  };

  const onDeleteCategory = async (
    rowIndex: number,
    row: TimeReportingProjectFormTypes.ProjectCategory,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>
  ) => {
    const { values, setFieldValue, setFieldTouched } = formikProps;
    const filteredProjectCategories = values.projectCategories?.filter((pc) => row.name !== pc.name);
    setFieldValue(FormFieldConstants.TimeReportingProject.Create.ProjectCategories, filteredProjectCategories);
    setFieldTouched(FormFieldConstants.TimeReportingProject.Create.ProjectCategories, true, false);

    const filteredProjectCategoryMembers = values.projectCategoryMembers?.filter(
      // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
      (pcm) => pcm.projectCategory?.name != row.name
    );

    setFieldValue(
      FormFieldConstants.TimeReportingProject.Create.ProjectCategoryMembers,
      filteredProjectCategoryMembers
    );

    const updatedProjectMembers = values.projectMembers?.map((member) => ({
      ...member,
      selfBillingHourlyRates: member.selfBillingHourlyRates?.filter((rate) => rate.categoryName !== row.name),
    }));
    setFieldValue(FormFieldConstants.TimeReportingProject.Create.ProjectMembers, updatedProjectMembers);
  };

  const onDeactivateCategory = async (
    rowIndex: number,
    row: TimeReportingProjectFormTypes.ProjectCategory,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>
  ) => {
    const { values, setFieldValue, setFieldTouched } = formikProps;

    let inactiveDate = DateHelper.addDays(new Date(), -1);
    let categoryActivity = false;

    const updatedRows = values.projectCategories?.map((pc) => {
      if (pc.name === row.name) {
        if (DateHelper.isBefore(new Date(inactiveDate), new Date(values.projectStartDate))) {
          inactiveDate = values.projectStartDate;
          categoryActivity = true;
        }

        return {
          ...pc,
          endDate: inactiveDate,
          isCatgoryActive: categoryActivity,
        } as TimeReportingProjectFormTypes.ProjectCategory;
      }
      return pc;
    });
    setFieldValue(FormFieldConstants.TimeReportingProject.Create.ProjectCategories, updatedRows);
    setFieldTouched(FormFieldConstants.TimeReportingProject.Create.ProjectCategories, true, false);
  };

  const onActivateCategory = async (
    rowIndex: number,
    row: TimeReportingProjectFormTypes.ProjectCategory,
    formikProps: FormikProps<TimeReportingProjectFormTypes.Create>
  ) => {
    const { values, setFieldValue, setFieldTouched } = formikProps;

    const updatedRows = values.projectCategories?.map((pc) =>
      pc.name === row.name
        ? ({ ...pc, endDate: undefined, isCatgoryActive: true } as TimeReportingProjectFormTypes.ProjectCategory)
        : pc
    );
    setFieldValue(FormFieldConstants.TimeReportingProject.Create.ProjectCategories, updatedRows);
    setFieldTouched(FormFieldConstants.TimeReportingProject.Create.ProjectCategories, true, false);
  };

  return [
    {
      label: "",
      descriptionText: TranslationKeys.TimeReportingProject.ProjectCategoriesExplanationDescription,
      type: FormEnums.FormSectionType.FIELDGROUPSECTION,
      innerFields: {
        selfBillingMissingHint: {
          label: "",
          type: FormEnums.FormFieldType.CUSTOM,
          customField: <SelfBillingMissingRateInfoBanner />,
          readonly: true,
        },
        projectCategories: {
          label: TranslationKeys.Common.Categories,
          type: FormEnums.FormFieldType.TABLELIST,
          extraProps: {
            addItemForm: (values: TimeReportingProjectFormTypes.Create) => (
              <TimeReportingProjectCategoryFormContainer
                project={values}
                initialProject={props.defaultValues}
                handleSubmit={props.onCategorySubmitForm}
                isSalaryDeviationActive={props.isSalaryDeviationActive}
                otherDeviationTypes={props.otherDeviationTypes}
              />
            ),
            fullWidth: true,
            maxWidth: "md",
            hideHeaderDividerInForm: true,
            entityText: TranslationKeys.Common.Categories,
            activeKey: FormFieldConstants.TimeReportingProject.ProjectCategory.IsCatgoryActive,
            disableSort: true,
            idFieldKey: FormFieldConstants.TimeReportingProject.ProjectCategory.Name,
            moreActions: getCategoriesMoreActions,
            editItemButtonText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Done),
            confirmCloseOnX: true,
            resultTableProperties: {
              name: {
                formatter: (category: TimeReportingProjectFormTypes.ProjectCategory) => (
                  <Grid item container direction="column">
                    <Grid className={cssExports.tableListMainText}>{category.name}</Grid>
                    {props.getProjectCategoryDetailView && props.getProjectCategoryDetailView(category)}
                  </Grid>
                ),
              },
              billingModel: {
                formatter: (currentCategory: TimeReportingProjectFormTypes.ProjectCategory) => (
                  <Grid item container direction="column">
                    <Grid className={cssExports.tableListMainText}>
                      {LocalizationHelperInstance.getTranslatedEnumValue(
                        "TimeReportingProject.CategoryInvoiceType",
                        currentCategory.billingModel
                      )}
                    </Grid>
                    <Grid>
                      {currentCategory.defaultHourlyRate !== undefined
                        ? `${currentCategory.defaultHourlyRate} ${
                            props.currency
                          }/${LocalizationHelperInstance.getFormattedMessage(
                            TranslationKeys.TimeReportingProject.ShortHourDescription
                          )}`
                        : ""}
                    </Grid>
                  </Grid>
                ),
              },
              isDefault: {
                formatter: (value: TimeReportingProjectFormTypes.ProjectCategory) => (
                  <ProjectCategoryStatusTags category={value}></ProjectCategoryStatusTags>
                ),
              },
            },
            hideDeleteItem: true,
          } as TableListFieldProps<TimeReportingProjectFormTypes.ProjectCategory>,
        },
      },
    },
  ];
}

export default useProjectCategoryFields;
